import { TFunction } from 'i18next';
import { TableColumn } from 'react-data-table-component';
import { NavigateFunction } from 'react-router-dom';
import { getOperationStatusTranslationKey } from 'src/api/wf/models/enums/WfOperationtStatus';
import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';
import LinkButton from '../../common/button/LinkButton';
import { wfNavigate } from '../../router/WfScreenRegistration';
import { WfScreensMap } from '../../router/WfScreensMap';
import { WF_Screen } from 'src/ui/wf/router/WfScreens';

export const propertyListCustomStyles = {
  table: {
    style: {
      border: '1px solid #d1d5db',
    },
  },
  headCells: {
    style: {
      borderRight: '1px solid #d1d5db',
      fontWeight: 'bold',
      textAlign: 'center' as const,
      padding: '0',
    },
  },
  cells: {
    style: {
      borderRight: '1px solid #d1d5db',
    },
  },
  rows: {
    style: {
      borderBottom: '1px solid #d1d5db',
    },
  },
  selectableRowsStyle: {
    backgroundColor: '#e0f2fe',
    '&:hover': {
      backgroundColor: '#bae6fd',
      cursor: 'pointer',
    },
  },
};

export const getPropertyListColumns = (
  t: TFunction,
  navigate: NavigateFunction,
  setSelectedWorkplace: (selectedProduct: WfWorkplace | null) => void
): TableColumn<WfWorkplace>[] => [
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">{t('wf.propertyList.reservations')}</div>
    ),
    selector: (row) =>
      row.details.reservations.length !== 0 ? t('wf.propertyList.yes') : t('wf.propertyList.no'),
    width: '3rem',
  },
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">
        {t('wf.propertyList.reservedEngineer')}
      </div>
    ),
    selector: (row) =>
      row.details.reservations.length !== 0 ? row.details.reservations[0].reserverName : '',
    width: '8rem',
  },
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">
        {t('wf.propertyList.assignedEngineer')}
      </div>
    ),
    selector: (row) =>
      row.details.reservations.length !== 0 ? row.details.reservations[0].engineerName : '',
    width: '8rem',
  },
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">
        {t('wf.propertyList.organizationCode')}
      </div>
    ),
    selector: (row) => row.organizationCode,
    width: '8rem',
  },
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">{t('wf.propertyList.salesOffice')}</div>
    ),
    selector: (row) => row.aliasSalesOfficeName,
    width: '8rem',
  },
  {
    name: <div className="size-full bg-orange-400 px-2 py-1">{t('wf.common.customerNumber')}</div>,
    selector: (row) => row.customerId,
    cell: (row) => (
      <LinkButton
        title={`${row.customerId}`}
        onClick={() => {
          setSelectedWorkplace(row);
          wfNavigate(navigate, WfScreensMap[WF_Screen.WorkplaceDetails].path);
        }}
        className="text-left"
      />
    ),
    width: '6rem',
  },
  {
    name: (
      <div className="size-full bg-orange-400 px-2 py-1">{t('wf.propertyList.buildingName')}</div>
    ),
    selector: (row) => `${row.emergencyClass ? '★ ' : ''}${row.name}`,
    width: '14rem',
    grow: 1.5,
    wrap: true,
  },
  {
    name: (
      <div className="size-full bg-yellow-300 px-2 py-1">
        {t('wf.propertyList.incompleteAlerts')}
      </div>
    ),
    selector: (row) => row.details.incompleteAlerts ?? '',
    width: '6rem',
  },
  {
    name: <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.onCall')}</div>,
    selector: (row) => row.details.onCall ?? '',
    width: '7.5rem',
  },
  {
    name: <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.alert')}</div>,
    selector: (row) => row.details.alert ?? '',
    width: '7.5rem',
  },
  {
    name: (
      <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.confinement')}</div>
    ),
    selector: (row) => row.details.confinement ?? '',
    width: '8rem',
  },
  {
    name: (
      <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.powerOutage')}</div>
    ),
    selector: (row) => row.details.powerOutage ?? '',
    width: '7rem',
  },
  {
    name: (
      <div className="size-full bg-blue-500 px-2 py-1">{t('wf.propertyList.floodRequired')}</div>
    ),
    selector: (row) =>
      row.details.floodRequired
        ? t('wf.propertyList.details.floodResponseRequiredTrue')
        : t('wf.propertyList.details.floodResponseRequiredFalse'),
    width: '4rem',
  },
  {
    name: (
      <div className="size-full bg-green-500 px-2 py-1">{t('wf.propertyList.partsRequired')}</div>
    ),
    selector: (row) =>
      row.details.partsRequired
        ? t('wf.propertyList.details.partsRequiredTrue')
        : t('wf.propertyList.details.partsRequiredFalse'),
    width: '4rem',
  },
  {
    name: <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.submitted')}</div>,
    selector: (row) =>
      row.details.submitted === true
        ? t('wf.propertyList.details.submittedTrue')
        : row.details.submitted === false
          ? t('wf.propertyList.details.submittedFalse')
          : '',
    width: '6rem',
  },
  {
    name: <div className="size-full bg-yellow-300 px-2 py-1">{t('wf.propertyList.status')}</div>,
    selector: (row) => t(getOperationStatusTranslationKey(row.details.statusId)),
    width: '11rem',
  },
];
